<template>
  <AccordionItem
    ref="accordionItem"
    :id="`secondary-filter-${index}`"
    class="filter filter--secondary"
    :class="{ 'filter--color': isColorFilter }"
    :index="index"
    :label="filter.title"
    :isTrackingClickEvent="false"
    :isActiveIndicatorVisible="hasSelectedOptions && !isAccordionOpen"
    @height-change="onAccordionHeightChange"
    @height-change-end="onAccordionHeightChangeEnd"
  >
    <!-- Sizes -->
    <div v-if="isSizeFilter" class="filter__values filter__values--sizes">
      <div class="filter__sizes">
        <div
          v-for="option in filter.options"
          :key="option.value"
          class="filter__size"
          :class="{ 'is-selected': selectedValues.includes(option.value) }"
          @click="selectOption(option)"
        >
          {{ option.label }}
        </div>
      </div>
      <div
        v-show="activeCardVariant !== undefined"
        class="filter__size-guide"
        @click.prevent="showSizeGuide"
      >
        {{ $t('filter.size_guide') }}
      </div>
    </div>
    <!-- Colors -->
    <div v-else-if="isColorFilter" class="filter__values filter__values--colors">
      <div
        v-for="option in filter.options"
        :key="option.value"
        class="filter__value color-selector"
        :class="`color-${option.value}`"
        @click="selectOption(option)"
      >
        <div
          class="color-selector__color-box"
          :class="{ 'is-selected': selectedValues.includes(option.value) }"
        >
          <div class="color-selector__color color-selector__color--primary"></div>
          <div class="color-selector__color color-selector__color--secondary"></div>
        </div>
      </div>
    </div>
    <!-- Normal -->
    <div class="filter__values" v-else>
      <div v-for="option in filter.options" :key="option.value" class="filter__value">
        <input
          type="checkbox"
          ref="secondaryCheckbox"
          class="checkbox-square"
          :id="`filter${option.value}`"
          :data-id="option.value"
          :value="option.value"
          :checked="selectedValues.includes(option.value)"
          @change="selectOption(option)"
        />
        <label :for="`filter${option.value}`">{{ option.label }}</label>
      </div>
    </div>
  </AccordionItem>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { GTM_EVENT_CATEGORIES } from '@/services/external-communication/gtm/gtm-events'
import AccordionItem from '@/components/_shared/AccordionItem/AccordionItem.vue'

export default {
  name: 'SecondaryFilter',
  components: {
    AccordionItem,
  },

  props: {
    filter: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isAccordionOpen: false,
    }
  },

  computed: {
    ...mapGetters({
      activeCardVariant: 'nav/activeCardVariant',
      activePrimaryFilters: 'filter/activePrimaryFilters',
      activeSecondaryFilters: 'filter/activeSecondaryFilters',
      activeGender: 'filter/activeGender',
      activeProductType: 'filter/activeProductType',
    }),

    isSizeFilter() {
      return this.filter.type === 'size'
    },

    isColorFilter() {
      return this.filter.type === 'color'
    },

    selectedFilter() {
      return this.activeSecondaryFilters.find(active => active.id === this.filter.id)
    },

    selectedValues() {
      return this.selectedFilter?.values || []
    },

    hasSelectedOptions() {
      return this.selectedValues.length > 0
    },
  },

  mounted() {
    if (this.hasSelectedOptions) this.$refs.accordionItem.isOpen = true
  },

  methods: {
    ...mapActions({
      showSizeGuideOverlay: 'overlay/showSizeGuideOverlay',
      setSecondaryFilter: 'filter/setSecondaryFilter',
      removeSecondaryFilter: 'filter/removeSecondaryFilter',
    }),

    showSizeGuide() {
      if (this.activeGender && this.activeProductType) {
        this.showSizeGuideOverlay({
          gender: this.activeGender,
          productType: this.activeProductType,
          isSizeFinderShown: false,
        })
      }
    },

    selectOption(option) {
      if (this.selectedValues.includes(option.value)) {
        // remove
        this.removeSecondaryFilter({
          id: this.filter.id,
          value: option.value,
          order: this.filter.order,
        })
      } else {
        // add
        this.setSecondaryFilter({
          ...this.filter,
          values: [option.value],
        })

        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.filterAndSorting,
          details: {
            category: GTM_EVENT_CATEGORIES.filterSorting,
            action: this.filter.id,
            label: option.value,
          },
        })
      }
    },

    onAccordionHeightChange(additionalHeight) {
      this.isAccordionOpen = additionalHeight > 0

      if (this.isAccordionOpen) {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.filterAndSorting,
          details: {
            category: GTM_EVENT_CATEGORIES.filterSorting,
            action: this.filter.id,
            label: 'expand',
          },
        })
      }

      this.$emit('height-change', additionalHeight)
    },

    onAccordionHeightChangeEnd() {
      this.$emit('height-change-end')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../Filter';
</style>
